import axios from 'axios';
import cache from './helpscoutCache';

// const HELPSCOUT_API_BASE_URL = 'https://docsapi.helpscout.net/v1';
const HELPSCOUT_API_BASE_URL = process.env.VUE_APP_API_ENDPOINT;

/**
 * HelpScout Docs API Service
 * 
 * Authentication: Uses API key from environment variable VUE_APP_HELPSCOUT_API_KEY
 * The API key should be set in .env.local file
 */
class HelpScoutService {
  constructor() {
    this.apiKey = process.env.VUE_APP_HELPSCOUT_API_KEY;
    this.siteId = process.env.VUE_APP_HELPSCOUT_SITE_ID;

    // Create axios instance with basic auth
    this.client = axios.create({
      baseURL: HELPSCOUT_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Make a cached API request
   */
  async cachedRequest(endpoint, params = {}, cacheTTL) {
    const cacheKey = cache.generateKey(endpoint, params);
    const cached = cache.get(cacheKey);
    if (cached) {
      console.log(`[Cache HIT] ${endpoint}`);
      return cached;
    }

    console.log(`[Cache MISS] ${endpoint}`);
    const response = await this.client.get(endpoint, { params });
    cache.set(cacheKey, response.data, cacheTTL);
    return response.data;
  }

  /**
   * List all collections (documentation sites)
   * @param {number} page - Page number (default: 1)
   * @returns {Promise} Collections data
   */
  async getCollections(page = 1) {
    try {
      const params = { page };
      if (this.siteId) {
        params.siteId = this.siteId;
      }
      return await this.cachedRequest('/collections', params, 10 * 60 * 1000); // Cache for 10 minutes
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw error;
    }
  }

  /**
   * Get a specific collection by ID
   * @param {string} collectionId - Collection ID
   * @returns {Promise} Collection data
   */
  async getCollection(collectionId) {
    try {
      return await this.cachedRequest(`/collections/${collectionId}`, {}, 10 * 60 * 1000);
    } catch (error) {
      console.error('Error fetching collection:', error);
      throw error;
    }
  }

  /**
   * List all categories for a collection
   * @param {string} collectionId - Collection ID
   * @param {number} page - Page number (default: 1)
   * @returns {Promise} Categories data
   */
  async getCategories(collectionId, page = 1) {
    try {
      const params = { page };
      if (this.siteId) {
        params.siteId = this.siteId;
      }
      return await this.cachedRequest(`/collections/${collectionId}/categories`, params, 5 * 60 * 1000);
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Get a specific category by ID
   * @param {string} categoryId - Category ID
   * @returns {Promise} Category data
   */
  async getCategory(categoryId) {
    try {
      return await this.cachedRequest(`/categories/${categoryId}`, {}, 5 * 60 * 1000);
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  }

  /**
   * List articles for a collection
   * @param {string} collectionId - Collection ID
   * @param {number} page - Page number (default: 1)
   * @param {string} status - Article status filter (all, published, notpublished)
   * @returns {Promise} Articles data
   */
  async getArticles(collectionId, page = 1, status = 'published') {
    try {
      const params = { page, status };
      if (this.siteId) {
        params.siteId = this.siteId;
      }
      return await this.cachedRequest(`/collections/${collectionId}/articles`, params, 3 * 60 * 1000);
    } catch (error) {
      console.error('Error fetching articles:', error);
      throw error;
    }
  }

  /**
   * List articles for a specific category
   * @param {string} categoryId - Category ID
   * @param {number} page - Page number (default: 1)
   * @param {string} status - Article status filter (all, published, notpublished)
   * @returns {Promise} Articles data
   */
  async getArticlesByCategory(categoryId, page = 1, status = 'published') {
    try {
      const params = { page, status };
      if (this.siteId) {
        params.siteId = this.siteId;
      }
      return await this.cachedRequest(`/categories/${categoryId}/articles`, params, 3 * 60 * 1000);
    } catch (error) {
      console.error('Error fetching articles by category:', error);
      throw error;
    }
  }

  /**
   * Get a specific article by ID
   * @param {string} articleId - Article ID
   * @param {boolean} draft - Whether to fetch draft version
   * @returns {Promise} Article data
   */
  async getArticle(articleId, draft = false) {
    try {
      return await this.cachedRequest(`/articles/${articleId}`, { draft }, 5 * 60 * 1000);
    } catch (error) {
      console.error('Error fetching article:', error);
      throw error;
    }
  }

  /**
   * Get related articles for a specific article
   * @param {string} articleId - Article ID
   * @param {number} page - Page number (default: 1)
   * @param {string} status - Article status filter (all, published, notpublished)
   * @returns {Promise} Related articles data
   */
  async getRelatedArticles(articleId, page = 1, status = 'published') {
    try {
      const params = { page, status };
      if (this.siteId) {
        params.siteId = this.siteId;
      }
      return await this.cachedRequest(`/articles/${articleId}/related`, params, 5 * 60 * 1000);
    } catch (error) {
      console.error('Error fetching related articles:', error);
      throw error;
    }
  }

  /**
   * Search articles across all collections
   * @param {string} query - Search query
   * @param {number} page - Page number (default: 1)
   * @returns {Promise} Search results
   */
  async searchArticles(query, page = 1) {
    try {
      const params = { query, page };
      if (this.siteId) {
        params.siteId = this.siteId;
      }
      return await this.cachedRequest('/articles/search', params, 2 * 60 * 1000);
    } catch (error) {
      console.error('Error searching articles:', error);
      throw error;
    }
  }
}

export default new HelpScoutService();

